import React, { lazy, memo, useLayoutEffect } from 'react';
import { dispatch, useReframe } from 'nike-re-framejs';

import i18n from '@i18n';
import { IOLoadableCasinoSportBookModule } from '@/casinovegas/views/LoadableCasinoSportBookModule';
import { IOLiveStreamsModule } from '@/nike-tv/views/LiveStreamsModule';
import useCashinOffer from '@/nikeone/hook/useCashinOffer';
import { IOSubscriptionMatches } from '@/notifications/components/homepage/SubscriptionMatches';
import { timeToHide } from '@/virtualgames/services/vh-service';
import Odstavka from './Odstavka';
import Stories from './Stories';
import FinishRegistration from './FinishRegistration';
import { IOBetbookModule } from './module/BetbookModule';
import VirtualGamesModule from './module/VirtualGamesModule';
import imports from '@/routing/imports';
import CharityCounterPending from '@/charity/views/CharityCounterPending';
import NavButtons from '@/nikeone/components/NavButtons';
import { accountBonuses$, accountForcedRequestedKyc$, accountRegistrationStatus$, loggedIn$ } from '@/account/subs';
import NavLinks, { setNavLinklabel } from '@/nikeone/components/NavLinks';
import TournamentCupIcon from '@/icons/TournamentCupIcon';
import { BONUS_DASHBOARD_CURRENT, TOURNAMENTS } from '@/routing/routes';
import BonusIcon from '@/icons/BonusIcon';
import { activeTournamentCount$, tournament$$, tournamentsActive$ } from '@/tournaments/subs';
import { List, Map } from 'immutable';
import { IONaj10Top5Module } from '@/home/views/module/Naj10Top5Module';
import BetLocationContext, { betLocation } from 'nike-ui/src/common/BetLocationContext';
import { isProfileBetbookMaintenance, isProfileNewHomepageEnabled } from 'nike-ui/src/profiles';
import { showLuckyWheelBanner$ } from 'nike-ui/src/components/lucky-wheel/h&s';
import LuckyWheelBanner from 'nike-ui/src/components/lucky-wheel/LuckyWheelBanner';
import { openLuckyWheelModal } from '@/luckyWheel/h&s';
import Loadable from '@/common/lazy/Loadable';
import clsx from 'clsx';
import KycUserAnnouncement from '@/home/views/KycUserAnnouncement';
import HomepageKycDialog from 'nike-ui/src/components/homepage/components/HomePageKycDialog';
import { openDialog } from 'nike-ui/src/components/dialogs/dialogs';
import { getObject, removeKey, setObject } from '@/services/localStorage';
import KycBlockedAnnouncement from '@/home/views/KycBlockedAnnouncement';

const CharityCounter = lazy(imports.charityCounter);

//TODO: ked sa vypne profil new-homepage (pojde do prod new hp), tento dynamicky import sa zrusi
const NewHomepageLoadable = Loadable({ loader: imports.homepage });

function HomePage() {
    useCashinOffer();
    const { subscribe } = useReframe();
    const loggedIn = subscribe(loggedIn$);
    const activeTournaments = subscribe(tournamentsActive$) || List();
    const activeTournamentsCount = subscribe(activeTournamentCount$);
    const tournament = subscribe(tournament$$(activeTournaments[0])) || Map();
    const tournamentName = tournament.name;
    const bonuses = subscribe(accountBonuses$) || Map();
    const activeBonusCount = bonuses.activeCount + bonuses.offeredCount;
    const showLuckyWheelBanner = subscribe(showLuckyWheelBanner$);
    const newHP = isProfileNewHomepageEnabled();
    const forcedRequestedKyc = subscribe(accountForcedRequestedKyc$);
    const registrationStatus = subscribe(accountRegistrationStatus$);
    const kycUserAnnouncementDialog = forcedRequestedKyc ? <KycUserAnnouncement /> : null;

    useLayoutEffect(() => {
        if (loggedIn) {
            const getKycDialogLS = getObject('showKycDialog');
            if (!getKycDialogLS) {
                if (registrationStatus === 'KYCblocked') {
                    openDialog(HomepageKycDialog, { postfix: registrationStatus });
                } else if (forcedRequestedKyc) {
                    openDialog(HomepageKycDialog);
                }
            }
            setObject('showKycDialog', 1);
        } else {
            removeKey('showKycDialog');
        }
    }, [forcedRequestedKyc, loggedIn, registrationStatus]);

    return (
        <div className={clsx('overflow-hidden', { 'bg-black-5': newHP })}>
            <div className={newHP ? 'px-20' : 'px-15'}>
                <Odstavka />
                <FinishRegistration />
                {registrationStatus === 'KYCblocked' ? <KycBlockedAnnouncement /> : kycUserAnnouncementDialog}
            </div>
            <NavButtons />
            {newHP ? (
                <NewHomepageLoadable />
            ) : (
                <>
                    {showLuckyWheelBanner ? (
                        <div className="lucky-wheel-home-page pl-15 pr-15">
                            <LuckyWheelBanner
                                oneLine
                                luckyWheelDescription={i18n('luckyWheel.banner.subtitle')}
                                luckyWheelName={i18n('luckyWheel.banner.title')}
                                onClick={() => dispatch(openLuckyWheelModal)}
                            />
                        </div>
                    ) : null}
                    {loggedIn ? (
                        <div className="mb-15">
                            {activeTournamentsCount > 0 ? (
                                <NavLinks
                                    route={TOURNAMENTS}
                                    icon={<TournamentCupIcon width="20" height="20" />}
                                    label={setNavLinklabel(tournamentName, activeTournamentsCount, 'Turnaje')}
                                />
                            ) : null}
                            {activeBonusCount > 0 ? (
                                <NavLinks
                                    route={BONUS_DASHBOARD_CURRENT}
                                    icon={<BonusIcon width="20" height="20" />}
                                    label={setNavLinklabel(bonuses.activeBonus?.name, activeBonusCount, 'Bonusy')}
                                />
                            ) : null}
                        </div>
                    ) : null}

                    <BetLocationContext.Provider value={{ betLocationSection: betLocation.HP_BANNER }}>
                        <Stories />
                    </BetLocationContext.Provider>
                    {timeToHide ? null : <VirtualGamesModule />}
                    <IONaj10Top5Module />
                    <React.Suspense fallback={<CharityCounterPending />}>
                        <a
                            href={i18n('charity.counter.url')}
                            target="_self"
                            rel="noopener noreferrer"
                            title={i18n('charity.counter.title')}
                        >
                            <CharityCounter />
                        </a>
                    </React.Suspense>
                    <IOLoadableCasinoSportBookModule />
                    {!isProfileBetbookMaintenance() && <IOBetbookModule />}
                    <IOLiveStreamsModule />
                    {loggedIn ? <IOSubscriptionMatches /> : null}
                </>
            )}
        </div>
    );
}

export default memo(HomePage);
